// ============================================
// POSITION CARD COMPONENT
// Displays a single tracked position
// ============================================

import React, { useState } from 'react';
import { ActivePosition } from '../types';
import {
  TrendingUp,
  TrendingDown,
  Target,
  AlertTriangle,
  Calendar,
  Clock,
  DollarSign,
  Bell,
  BellOff,
  X,
  ChevronDown,
  ChevronUp,
  Edit3,
  Trash2
} from 'lucide-react';

interface PositionCardProps {
  position: ActivePosition;
  onClose: (positionId: number, pnl?: number) => void;
  onDelete: (positionId: number) => void;
  onToggleAlerts: (positionId: number, enabled: boolean) => void;
  onUpdateNotes: (positionId: number, notes: string) => void;
}

export const PositionCard: React.FC<PositionCardProps> = ({
  position,
  onClose,
  onDelete,
  onToggleAlerts,
  onUpdateNotes
}) => {
  const [expanded, setExpanded] = useState(false);
  const [editingNotes, setEditingNotes] = useState(false);
  const [notes, setNotes] = useState(position.notes || '');
  const [showCloseModal, setShowCloseModal] = useState(false);
  const [closePrice, setClosePrice] = useState('');
  const [closePnL, setClosePnL] = useState('');

  const pnl = position.currentPnL || 0;
  const pnlPercent = position.currentPnLPercent || 0;
  const isProfitable = pnl >= 0;

  // Calculate DTE
  const daysToExpiry = position.expirationDate
    ? Math.ceil((new Date(position.expirationDate).getTime() - Date.now()) / (1000 * 60 * 60 * 24))
    : null;

  // Status colors
  const statusColors: Record<string, string> = {
    ACTIVE: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/30',
    CLOSED_PROFIT: 'bg-cyan-500/10 text-cyan-400 border-cyan-500/30',
    CLOSED_LOSS: 'bg-rose-500/10 text-rose-400 border-rose-500/30',
    CLOSED_MANUAL: 'bg-slate-500/10 text-slate-400 border-slate-500/30',
    EXPIRED: 'bg-amber-500/10 text-amber-400 border-amber-500/30'
  };

  const directionColors: Record<string, string> = {
    BULLISH: 'text-emerald-400',
    BEARISH: 'text-rose-400',
    NEUTRAL: 'text-amber-400'
  };

  const handleSaveNotes = () => {
    onUpdateNotes(position.id!, notes);
    setEditingNotes(false);
  };

  const handleClose = () => {
    const pnlValue = closePnL ? parseFloat(closePnL) : position.currentPnL;
    onClose(position.id!, pnlValue);
    setShowCloseModal(false);
  };

  return (
    <div className="bg-terminal-card border border-terminal-border rounded-xl overflow-hidden">
      {/* Header Row */}
      <div
        className="p-4 cursor-pointer hover:bg-slate-800/50 transition-colors"
        onClick={() => setExpanded(!expanded)}
      >
        {/* Mobile: Stack layout / Desktop: Horizontal */}
        <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-0 sm:justify-between">

          {/* Top Row (Mobile) / Left Section (Desktop): Ticker, Direction, Status */}
          <div className="flex items-center justify-between sm:justify-start gap-3 sm:gap-4">
            <div className="flex items-center gap-3">
              <div>
                <div className="flex items-center gap-2">
                  <span className="text-lg sm:text-xl font-bold text-white">{position.ticker}</span>
                  <span className={`text-sm font-medium ${directionColors[position.direction]}`}>
                    {position.direction === 'BULLISH' ? <TrendingUp className="w-4 h-4 inline" /> : position.direction === 'BEARISH' ? <TrendingDown className="w-4 h-4 inline" /> : null}
                    <span className="hidden xs:inline ml-1">{position.direction}</span>
                  </span>
                </div>
                <div className="text-sm text-terminal-dim">{position.strategyName}</div>
              </div>
              {/* Status Badge */}
              <span className={`px-2 py-0.5 text-xs font-bold rounded border hidden sm:inline ${statusColors[position.status]}`}>
                {position.status.replace('_', ' ')}
              </span>
            </div>

            {/* Mobile-only: Actions on first row */}
            <div className="flex items-center gap-2 sm:hidden">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onToggleAlerts(position.id!, !position.alertsEnabled);
                }}
                className={`p-2.5 rounded-lg transition-colors ${position.alertsEnabled ? 'bg-cyan-500/10 text-cyan-400' : 'bg-slate-800 text-terminal-dim'}`}
                title={position.alertsEnabled ? 'Alerts enabled' : 'Alerts disabled'}
              >
                {position.alertsEnabled ? <Bell className="w-5 h-5" /> : <BellOff className="w-5 h-5" />}
              </button>
              {expanded ? <ChevronUp className="w-5 h-5 text-terminal-dim" /> : <ChevronDown className="w-5 h-5 text-terminal-dim" />}
            </div>
          </div>

          {/* Bottom Row (Mobile) / Center+Right Section (Desktop) */}
          <div className="flex items-center justify-between sm:flex-1 sm:justify-end sm:gap-8">
            {/* P&L Display */}
            <div className="flex items-center gap-3 sm:text-center">
              <div className={`text-xl sm:text-2xl font-bold ${isProfitable ? 'text-emerald-400' : 'text-rose-400'}`}>
                {isProfitable ? '+' : ''}{pnl.toFixed(0)}
                <span className="text-sm ml-1">USD</span>
              </div>
              <div className={`text-sm ${isProfitable ? 'text-emerald-400/70' : 'text-rose-400/70'}`}>
                {isProfitable ? '+' : ''}{pnlPercent.toFixed(1)}%
              </div>
            </div>

            {/* DTE & Desktop Actions */}
            <div className="flex items-center gap-3 sm:gap-4">
              {daysToExpiry !== null && (
                <div className={`flex sm:flex-col items-center gap-1 sm:gap-0 ${daysToExpiry <= 7 ? 'text-rose-400' : daysToExpiry <= 14 ? 'text-amber-400' : 'text-terminal-dim'}`}>
                  <div className="text-base sm:text-lg font-bold">{daysToExpiry}</div>
                  <div className="text-xs uppercase">DTE</div>
                </div>
              )}

              {/* Status Badge - Mobile only shows here */}
              <span className={`px-2 py-0.5 text-xs font-bold rounded border sm:hidden ${statusColors[position.status]}`}>
                {position.status.replace('_', ' ')}
              </span>

              {/* Desktop-only: Alert Toggle & Expand */}
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onToggleAlerts(position.id!, !position.alertsEnabled);
                }}
                className={`p-2.5 rounded-lg transition-colors hidden sm:block ${position.alertsEnabled ? 'bg-cyan-500/10 text-cyan-400' : 'bg-slate-800 text-terminal-dim'}`}
                title={position.alertsEnabled ? 'Alerts enabled' : 'Alerts disabled'}
              >
                {position.alertsEnabled ? <Bell className="w-5 h-5" /> : <BellOff className="w-5 h-5" />}
              </button>
              <div className="hidden sm:block">
                {expanded ? <ChevronUp className="w-5 h-5 text-terminal-dim" /> : <ChevronDown className="w-5 h-5 text-terminal-dim" />}
              </div>
            </div>
          </div>
        </div>

        {/* Progress Bar - Profit Target Progress */}
        {position.status === 'ACTIVE' && (
          <div className="mt-3">
            <div className="flex justify-between text-xs text-terminal-dim mb-1">
              <span>Max Loss: -${position.maxLoss}</span>
              <span>Current</span>
              <span>Max Profit: +${position.maxProfit}</span>
            </div>
            <div className="h-2 bg-slate-800 rounded-full overflow-hidden relative">
              {/* Max loss marker */}
              <div className="absolute left-0 top-0 bottom-0 w-0.5 bg-rose-500" />
              {/* Max profit marker */}
              <div className="absolute right-0 top-0 bottom-0 w-0.5 bg-emerald-500" />
              {/* 50% profit marker */}
              <div className="absolute left-3/4 top-0 bottom-0 w-0.5 bg-amber-500/50" />
              {/* Current position */}
              <div
                className={`h-full rounded-full transition-all ${isProfitable ? 'bg-gradient-to-r from-emerald-600 to-emerald-400' : 'bg-gradient-to-r from-rose-600 to-rose-400'}`}
                style={{
                  width: `${Math.min(100, Math.max(0, 50 + (pnlPercent / 2)))}%`,
                  marginLeft: pnlPercent < 0 ? `${50 + (pnlPercent / 2)}%` : '50%'
                }}
              />
            </div>
          </div>
        )}
      </div>

      {/* Expanded Details */}
      {expanded && (
        <div className="border-t border-terminal-border p-4 space-y-4 bg-slate-900/50">
          {/* Trade Details Grid */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-slate-800/50 p-3 rounded-lg">
              <div className="text-xs text-terminal-dim uppercase mb-1">Entry Price</div>
              <div className="text-white font-bold">${position.entryPrice.toFixed(2)}</div>
              <div className="text-xs text-terminal-dim">
                {position.creditDebit === 'CREDIT' ? 'Credit' : 'Debit'}
              </div>
            </div>
            <div className="bg-slate-800/50 p-3 rounded-lg">
              <div className="text-xs text-terminal-dim uppercase mb-1">Contracts</div>
              <div className="text-white font-bold">{position.contracts}</div>
              <div className="text-xs text-terminal-dim">
                {position.legs.length} leg{position.legs.length > 1 ? 's' : ''}
              </div>
            </div>
            <div className="bg-slate-800/50 p-3 rounded-lg">
              <div className="text-xs text-terminal-dim uppercase mb-1">Stop Loss</div>
              <div className="text-rose-400 font-bold">${position.stopLossPrice.toFixed(2)}</div>
              {position.trailingStopEnabled && position.trailingStopPrice && (
                <div className="text-xs text-amber-400">
                  Trail: ${position.trailingStopPrice.toFixed(2)}
                </div>
              )}
            </div>
            <div className="bg-slate-800/50 p-3 rounded-lg">
              <div className="text-xs text-terminal-dim uppercase mb-1">Profit Targets</div>
              <div className="text-emerald-400 font-bold text-sm">
                50%: ${position.profitTarget50.toFixed(2)}
              </div>
              <div className="text-xs text-emerald-400/70">
                100%: ${position.profitTarget100.toFixed(2)}
              </div>
            </div>
          </div>

          {/* Legs */}
          <div className="bg-slate-800/50 rounded-lg p-3">
            <div className="text-xs text-terminal-dim uppercase mb-2">Legs</div>
            <div className="space-y-1">
              {position.legs.map((leg, i) => (
                <div key={i} className="flex items-center gap-2 text-sm">
                  <span className={`px-1.5 py-0.5 rounded text-xs font-bold ${leg.action === 'BUY' ? 'bg-emerald-500/20 text-emerald-400' : 'bg-rose-500/20 text-rose-400'}`}>
                    {leg.action}
                  </span>
                  <span className="text-white">{leg.amount || 1}x</span>
                  <span className="text-terminal-dim">${leg.strike}</span>
                  <span className={leg.type === 'CALL' ? 'text-cyan-400' : 'text-amber-400'}>{leg.type}</span>
                  <span className="text-terminal-dim">{leg.expiry}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Notes */}
          <div className="bg-slate-800/50 rounded-lg p-3">
            <div className="flex items-center justify-between mb-2">
              <div className="text-xs text-terminal-dim uppercase">Notes</div>
              <button
                onClick={() => setEditingNotes(!editingNotes)}
                className="text-terminal-dim hover:text-white transition-colors"
              >
                <Edit3 className="w-4 h-4" />
              </button>
            </div>
            {editingNotes ? (
              <div className="space-y-2">
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  className="w-full bg-slate-900 border border-terminal-border rounded-lg p-2 text-white text-sm resize-none"
                  rows={3}
                  placeholder="Add notes about this trade..."
                />
                <div className="flex gap-2">
                  <button
                    onClick={handleSaveNotes}
                    className="px-3 py-1 bg-cyan-500/20 text-cyan-400 rounded text-sm hover:bg-cyan-500/30"
                  >
                    Save
                  </button>
                  <button
                    onClick={() => {
                      setNotes(position.notes || '');
                      setEditingNotes(false);
                    }}
                    className="px-3 py-1 bg-slate-700 text-terminal-dim rounded text-sm hover:bg-slate-600"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <div className="text-sm text-white">
                {position.notes || <span className="text-terminal-dim italic">No notes</span>}
              </div>
            )}
          </div>

          {/* Timestamps */}
          <div className="flex items-center gap-4 text-xs text-terminal-dim">
            <div className="flex items-center gap-1">
              <Calendar className="w-3 h-3" />
              Opened: {new Date(position.entryDate).toLocaleDateString()}
            </div>
            {position.lastPriceUpdate && (
              <div className="flex items-center gap-1">
                <Clock className="w-3 h-3" />
                Updated: {new Date(position.lastPriceUpdate).toLocaleTimeString()}
              </div>
            )}
          </div>

          {/* Action Buttons */}
          {position.status === 'ACTIVE' && (
            <div className="flex items-center gap-2 pt-2 border-t border-terminal-border">
              <button
                onClick={() => setShowCloseModal(true)}
                className="flex-1 py-2 bg-cyan-500/10 text-cyan-400 rounded-lg font-medium hover:bg-cyan-500/20 transition-colors flex items-center justify-center gap-2"
              >
                <Target className="w-4 h-4" />
                Close Position
              </button>
              <button
                onClick={() => onDelete(position.id!)}
                className="p-2 bg-rose-500/10 text-rose-400 rounded-lg hover:bg-rose-500/20 transition-colors"
                title="Delete position"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          )}
        </div>
      )}

      {/* Close Position Modal */}
      {showCloseModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="bg-terminal-card border border-terminal-border rounded-xl p-6 max-w-md w-full">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-white">Close Position</h3>
              <button onClick={() => setShowCloseModal(false)} className="text-terminal-dim hover:text-white">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm text-terminal-dim mb-1">Close Price (optional)</label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-terminal-dim">$</span>
                  <input
                    type="number"
                    step="0.01"
                    value={closePrice}
                    onChange={(e) => setClosePrice(e.target.value)}
                    className="w-full bg-slate-900 border border-terminal-border rounded-lg pl-7 pr-4 py-2 text-white"
                    placeholder={position.currentPrice?.toFixed(2) || '0.00'}
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm text-terminal-dim mb-1">Final P&L (optional)</label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-terminal-dim">$</span>
                  <input
                    type="number"
                    step="0.01"
                    value={closePnL}
                    onChange={(e) => setClosePnL(e.target.value)}
                    className="w-full bg-slate-900 border border-terminal-border rounded-lg pl-7 pr-4 py-2 text-white"
                    placeholder={position.currentPnL?.toFixed(2) || '0.00'}
                  />
                </div>
              </div>

              <div className="flex gap-2">
                <button
                  onClick={handleClose}
                  className="flex-1 py-2 bg-gradient-to-r from-cyan-500 to-emerald-500 text-black font-bold rounded-lg hover:opacity-90"
                >
                  Confirm Close
                </button>
                <button
                  onClick={() => setShowCloseModal(false)}
                  className="px-4 py-2 bg-slate-700 text-white rounded-lg hover:bg-slate-600"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PositionCard;
